import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Input,
  LinearProgress,
  Stack,
} from "@mui/joy";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import CustomModal from "components/common/CustomModal";
import LinkV2 from "components/common/LinkV2";
import ContractDocument from "components/common/ContractDocument";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  type AmbassadorContractType,
  type AmbassadorContractKeyTermSection,
  type AmbassadorContractSignPreviewResult,
  getAmbassadorToken,
} from "api/ambassadorApi";
import { saveBlobAsFile } from "utils/fileUtils";
import distancingLogo from "static/image/common/distancing_logo.png";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const CONTRACT_TYPES: {
  value: AmbassadorContractType;
  label: string;
}[] = [
  { value: "freelancer", label: "프리랜서 (개인)" },
  { value: "business_general", label: "사업자 (일반과세)" },
  { value: "business_simplified", label: "사업자 (간이과세)" },
];

// 독립 웹 페이지 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
const inputSx = {
  width: "100%",
  height: "54px",
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  fontSize: "16px",
  borderColor: "background.level3",
  borderWidth: "1px",
  "--joy-focus-thickness": "0px",
  boxShadow: "0px 0px 0px transparent",
  "&:focus-within": {
    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
  },
};

// 전문을 끝까지 읽었다고 판정할 하단 여유(px) — 브라우저 서브픽셀 스크롤 오차 흡수.
const READ_BOTTOM_MARGIN_PX = 24;

// 이메일 형식 — 계약서 사본 수신 주소라 TLD 까지 요구한다 (서버도 동일 기준으로 재검증).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// 사업자등록번호 입력 자동 포맷 — 숫자만 남기고 000-00-00000 하이픈을 넣는다 (최대 10자리).
const formatBrnInput = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

// 다음(카카오) 우편번호 서비스 — 무료·키 불필요. 주소 오입력을 줄이기 위해 도로명주소는 검색으로만 입력받는다.
const DAUM_POSTCODE_SCRIPT_URL =
  "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";

let daumPostcodeLoader: Promise<void> | null = null;

// 스크립트 지연 로드 — 주소 검색을 처음 누를 때 1회만 불러온다 (실패 시 재시도 가능하도록 로더를 비운다).
function loadDaumPostcode(): Promise<void> {
  if ((window as any).daum?.Postcode) return Promise.resolve();
  if (!daumPostcodeLoader) {
    daumPostcodeLoader = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = DAUM_POSTCODE_SCRIPT_URL;
      script.onload = () => resolve();
      script.onerror = () => {
        daumPostcodeLoader = null;
        script.remove();
        reject(new Error("daum postcode load failed"));
      };
      document.head.appendChild(script);
    });
  }
  return daumPostcodeLoader;
}

type Step = "info" | "terms" | "read" | "done";

// 앰버서더 전자계약(약관 동의형) 체결 — 당사자 정보 입력 → 핵심 내용 확인 → 전문 스크롤 열람 → 체결.
// 핵심 내용(계약 기간·수수료·유효 결제·정산·콘텐츠 의무·주의)은 서버(keyTerms)가 내려준다 — 요약-본문 불일치 방지.
// 세무 처리·개인정보 동의는 정산 정보 수집 시점(서류 제출 단계)에 받는다.
// 계약서에는 당사자 특정 정보만 기재한다. 주민번호·계좌 등 정산 정보는 체결 후 서류 제출 단계(/ambassador/contract)에서 받는다.
// 전문은 서버가 병합해 내려주고, 체결 시에도 서버가 동일 입력으로 재병합해 저장한다 (본 것 = 저장되는 것).
export default function AmbassadorContractSign() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>("info");

  const [contractType, setContractType] =
    useState<AmbassadorContractType>("freelancer");
  const [realName, setRealName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [representativeName, setRepresentativeName] = useState("");
  const [businessRegistrationNumber, setBusinessRegistrationNumber] =
    useState("");
  // 주소 — 도로명주소는 다음 우편번호 검색으로만 입력, 상세주소는 직접 입력 (제출 시 합쳐서 전송)
  const [address, setAddress] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  // 주소 검색 시트 — 팝업(open)은 모바일·인앱 브라우저에서 차단될 수 있어 페이지 내 임베드로 띄운다
  const [addressSearchOpen, setAddressSearchOpen] = useState(false);
  const postcodeBoxRef = useRef<HTMLDivElement | null>(null);
  const addressDetailRef = useRef<HTMLInputElement | null>(null);
  const [contractEmail, setContractEmail] = useState("");

  const [preview, setPreview] =
    useState<AmbassadorContractSignPreviewResult | null>(null);
  // 전문 최하단 도달 여부 — 도달해야 체결 동의를 할 수 있다
  const [readCompleted, setReadCompleted] = useState(false);
  // 전문 읽기 진행률 (0~100) — 문서 상단 진행 바 표시용
  const [readProgress, setReadProgress] = useState(0);
  const readBoxRef = useRef<HTMLDivElement | null>(null);

  // 핵심 내용(계약 기간·수수료·유효 결제·정산·콘텐츠 의무·주의) 확인
  const [agreedKeyTerms, setAgreedKeyTerms] = useState(false);
  // 전문 확인·체결 포괄 동의 — 핵심 내용 확인 후 전문을 끝까지 읽고 마지막에 받는다
  const [agreedFull, setAgreedFull] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // 이미 체결된 경우 — 체결본 열람 뷰
  const [signedContent, setSignedContent] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [documentsSubmitted, setDocumentsSubmitted] = useState(false);

  const hasToken = !!getAmbassadorToken();

  // 진입 가드 + 프리필 — 이미 체결이면 체결본 열람, 외부에서 체결을 마친 건이면 기존 계약 페이지로.
  useEffect(() => {
    if (!hasToken) return;
    (async () => {
      try {
        const res = await ambassadorApi.getContract();
        if (res.code !== 0 || !res.result) return;
        const c = res.result;
        setCompleted(!!c.completed);
        setDocumentsSubmitted(!!c.bankbookFile);
        if (c.signedAt) {
          // 이미 전자서명 완료 — 체결본을 불러 열람 뷰로
          setSignedAt(c.signedAt);
          const signRes = await ambassadorApi.getContractSign();
          if (signRes.code === 0 && signRes.result?.signed) {
            setSignedContent(signRes.result.content ?? null);
            setSignedAt(signRes.result.signedAt ?? c.signedAt);
          }
          return;
        }
        if (c.completed) return; // 외부 체결 완료 건 — 아래 렌더에서 계약 페이지로 돌려보낸다
        // 구 플로우로 제출한 정보가 있으면 프리필
        if (c.type) setContractType(c.type);
        setRealName(c.realName ?? "");
        setBusinessName(c.businessName ?? "");
        setContractEmail(c.email ?? "");
      } catch (e) {
        // 401 은 인터셉터가 로그인으로 보낸다 — 그 외는 빈 폼으로 시작
      } finally {
        setLoading(false);
      }
    })();
  }, [hasToken]);

  // 전문이 화면보다 짧으면 스크롤 이벤트가 없다 — 렌더 후 한 번 판정한다
  useEffect(() => {
    if (step !== "read") return;
    const el = readBoxRef.current;
    if (el && el.scrollHeight <= el.clientHeight + READ_BOTTOM_MARGIN_PX) {
      setReadCompleted(true);
      setReadProgress(100);
    }
  }, [step, preview]);

  // 주소 검색 시트가 열리면 컨테이너에 우편번호 검색을 임베드한다.
  // 선택 시 도로명주소(없으면 지번주소)를 채우고 상세주소 입력으로 포커스를 넘긴다.
  useEffect(() => {
    if (!addressSearchOpen) return;
    const el = postcodeBoxRef.current;
    if (!el || !(window as any).daum?.Postcode) return;
    el.innerHTML = "";
    new (window as any).daum.Postcode({
      oncomplete: (data: { roadAddress?: string; jibunAddress?: string }) => {
        setAddress(data.roadAddress || data.jibunAddress || "");
        setAddressDetail("");
        setAddressSearchOpen(false);
        // 시트가 닫힌 뒤 상세주소로 포커스 — 모바일에서 바로 이어서 입력할 수 있게 한다
        setTimeout(() => addressDetailRef.current?.focus(), 150);
      },
      width: "100%",
      height: "100%",
    }).embed(el);
  }, [addressSearchOpen]);

  if (!hasToken) {
    // 로그인 후 이 페이지로 복귀하도록 출발지를 state 로 전달한다 (로그인 페이지가 해석)
    return (
      <Navigate
        to={PATH.ambassadorLogin}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // 외부에서 이미 체결한 건은 이 페이지의 서명 대상이 아니다 — 기존 계약 페이지(체결 사본 열람)로
  if (!loading && completed && !signedAt) {
    return <Navigate to={PATH.ambassadorContract} replace />;
  }

  const isBusiness = contractType !== "freelancer";
  const brnDigits = businessRegistrationNumber.replace(/\D/g, "");
  // 계약서에 기재될 최종 주소 — 검색된 도로명주소 + 상세주소
  const fullAddress = [address.trim(), addressDetail.trim()]
    .filter(Boolean)
    .join(", ");
  const emailValid = EMAIL_REGEX.test(contractEmail.trim());
  const infoFilled =
    (isBusiness
      ? businessName.trim().length > 0 &&
        representativeName.trim().length > 0 &&
        brnDigits.length === 10
      : realName.trim().length > 0) &&
    address.trim().length > 0 &&
    emailValid;

  const allAgreed = agreedKeyTerms && agreedFull;

  const signPartyParam = () => ({
    type: contractType,
    realName: isBusiness ? null : realName.trim(),
    businessName: isBusiness ? businessName.trim() : null,
    representativeName: isBusiness ? representativeName.trim() : null,
    businessRegistrationNumber: isBusiness ? brnDigits : null,
    address: fullAddress,
    email: contractEmail.trim(),
  });

  // 주소 검색 열기 — 스크립트 로드 후 시트를 띄운다 (임베드는 아래 useEffect 가 수행)
  const openAddressSearch = async () => {
    try {
      await loadDaumPostcode();
      setAddressSearchOpen(true);
    } catch (e) {
      setError("주소 검색을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.");
    }
  };

  const handlePreview = async () => {
    if (!infoFilled || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await ambassadorApi.previewContractSign(signPartyParam());
      if (res.code === 0 && res.result) {
        setPreview(res.result);
        // 정보를 고쳐 다시 넘어오면 동의 상태를 초기화한다 — 바뀐 내용에 대한 동의로 오인되지 않게
        setAgreedKeyTerms(false);
        setAgreedFull(false);
        setReadCompleted(false);
        setReadProgress(0);
        setStep("terms");
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSign = async () => {
    if (!preview || !allAgreed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await ambassadorApi.signContract({
        ...signPartyParam(),
        templateVersion: preview.templateVersion,
        agreedKeyTerms,
        agreedFull,
      });
      if (res.code === 0) {
        setConfirming(false);
        setStep("done");
        return;
      }
      setConfirming(false);
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setConfirming(false);
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const contractTypeLabel =
    CONTRACT_TYPES.find(({ value }) => value === contractType)?.label ??
    contractType;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "640px",
          px: "20px",
          py: "40px",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: "80px" }}>
            <CircularProgress color="neutral" />
          </Box>
        ) : signedContent != null || (signedAt && step !== "done") ? (
          // 이미 체결됨 — 체결본 열람·다운로드
          <SignedContractView
            content={signedContent}
            signedAt={signedAt}
            completed={completed}
            documentsSubmitted={documentsSubmitted}
            onHome={() => navigate(PATH.ambassador)}
            onDocuments={() => navigate(PATH.ambassadorContract)}
          />
        ) : step === "done" ? (
          <Stack spacing={4} alignItems="center" sx={{ pt: "48px" }}>
            <img
              src={distancingLogo}
              alt="distancing logo"
              style={{ width: "138px", height: "auto" }}
            />
            <Stack spacing={"12px"} alignItems="center">
              <CustomTypography
                variant="H3"
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"전자계약이 체결됐어요"}
              </CustomTypography>
              <CustomTypography
                variant="Body"
                sx={{
                  textAlign: "center",
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {
                  "체결된 계약서는 언제든 이 페이지에서 확인할 수 있어요.\n정산 정보·서류를 제출하면 담당자 확인 후 계약이 완료돼요."
                }
              </CustomTypography>
            </Stack>
            <Stack spacing={"12px"} alignItems="center" sx={{ width: "100%" }}>
              <Button
                variant="Action"
                size="lg"
                onClick={() => navigate(PATH.ambassadorContract)}
                sx={{
                  width: "100%",
                  maxWidth: "345px",
                  height: "54px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "16px",
                }}
              >
                {"정산 정보·서류 제출하기"}
              </Button>
              <LinkV2
                variant="Caption"
                text="홈으로 돌아가기"
                onClick={() => navigate(PATH.ambassador)}
              />
            </Stack>
          </Stack>
        ) : (
          <>
            {/* 뒤로가기는 상단 링크가 전담한다 — 단계 중에는 이전 단계로, 첫 단계에서는 홈으로.
                (하단 CTA 는 다음 단계로 가는 단일 동작만 남긴다) */}
            <Box sx={{ display: "flex" }}>
              <LinkV2
                variant="Caption"
                text={step === "info" ? "← 홈으로" : "← 이전으로"}
                onClick={() => {
                  setError(null);
                  if (step === "read") {
                    setStep("terms");
                    return;
                  }
                  if (step === "terms") {
                    setStep("info");
                    return;
                  }
                  navigate(PATH.ambassador);
                }}
              />
            </Box>
            <Stack spacing={"8px"} sx={{ mt: 2 }}>
              <CustomTypography
                variant="H3"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"앰버서더 활동 계약"}
              </CustomTypography>
              <CustomTypography
                variant="Caption"
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {step === "info"
                  ? "계약서에 기재할 정보를 입력해 주세요. (1/3)"
                  : step === "terms"
                    ? "앰버서더 활동의 핵심 내용이에요. 꼭 확인해 주세요. (2/3)"
                    : "계약서 전문을 끝까지 확인하면 체결할 수 있어요. (3/3)"}
              </CustomTypography>
            </Stack>

            {step === "info" && (
              <Stack spacing={4} sx={{ mt: 4 }}>
                <Stack spacing={"6px"}>
                  <FieldLabel text="계약형태" />
                  <Stack direction={"row"} sx={{ flexWrap: "wrap", gap: 1 }}>
                    {CONTRACT_TYPES.map(({ value, label }) => (
                      <Button
                        key={value}
                        variant="SingleSlim"
                        size="md"
                        color={contractType === value ? "primary" : "secondary"}
                        sx={{
                          transition: "background-color 0.3s ease",
                          whiteSpace: "nowrap",
                          fontSize: "16px",
                        }}
                        onClick={() => setContractType(value)}
                      >
                        {label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>

                {isBusiness ? (
                  <>
                    <Stack spacing={"6px"}>
                      <FieldLabel text="계약서에 기재할 사업자명" />
                      <Input
                        sx={inputSx}
                        placeholder="사업자등록증상 상호"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                      />
                    </Stack>
                    <Stack spacing={"6px"}>
                      <FieldLabel text="대표자명" />
                      <Input
                        sx={inputSx}
                        placeholder="사업자등록증상 대표자"
                        value={representativeName}
                        onChange={(e) => setRepresentativeName(e.target.value)}
                      />
                    </Stack>
                    <Stack spacing={"6px"}>
                      <FieldLabel text="사업자등록번호" />
                      <Input
                        sx={inputSx}
                        slotProps={{ input: { inputMode: "numeric" } }}
                        placeholder="000-00-00000"
                        value={businessRegistrationNumber}
                        onChange={(e) =>
                          setBusinessRegistrationNumber(
                            formatBrnInput(e.target.value)
                          )
                        }
                      />
                    </Stack>
                  </>
                ) : (
                  <Stack spacing={"6px"}>
                    <FieldLabel text="계약서에 기재할 실명" />
                    <Input
                      sx={inputSx}
                      placeholder="주민등록상 이름"
                      value={realName}
                      onChange={(e) => setRealName(e.target.value)}
                    />
                  </Stack>
                )}

                <Stack spacing={"6px"}>
                  <FieldLabel text={isBusiness ? "사업장 소재지" : "주소"} />
                  {/* 도로명주소는 다음 우편번호 검색으로만 입력 — 오입력 방지 (박스 전체가 클릭 영역, 별도 버튼 없음).
                      선택된 주소는 Input 이 아니라 줄바꿈되는 표시 박스로 보여준다 — 작은 화면에서 말줄임(...) 없이 전체 노출 */}
                  <Box
                    onClick={openAddressSearch}
                    sx={{
                      minHeight: "54px",
                      display: "flex",
                      alignItems: "center",
                      px: "14px",
                      py: "14px",
                      borderRadius: "12px",
                      backgroundColor: theme.vars.palette.Surface.Forward,
                      border: "1px solid",
                      borderColor: "background.level3",
                      cursor: "pointer",
                    }}
                  >
                    <CustomTypography
                      variant="Body"
                      sx={{
                        fontWeight: 500,
                        fontSize: "16px",
                        color: address
                          ? theme.vars.palette.Content.Strong
                          : theme.vars.palette.Content.Weak,
                        wordBreak: "keep-all",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {address || "주소를 입력하세요"}
                    </CustomTypography>
                  </Box>
                  {address && (
                    <Input
                      sx={inputSx}
                      slotProps={{ input: { ref: addressDetailRef } }}
                      placeholder="상세주소 (동·호수 등, 선택)"
                      value={addressDetail}
                      onChange={(e) => setAddressDetail(e.target.value)}
                    />
                  )}
                </Stack>

                <Stack spacing={"6px"}>
                  <FieldLabel text="계약서 사본을 받을 이메일" />
                  <Input
                    sx={inputSx}
                    type="email"
                    slotProps={{
                      input: { inputMode: "email", autoComplete: "email" },
                    }}
                    placeholder="email@example.com"
                    value={contractEmail}
                    onChange={(e) => setContractEmail(e.target.value)}
                  />
                  {contractEmail.trim().length > 0 && !emailValid && (
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.HighlightWithAlert,
                      }}
                    >
                      {"이메일 형식을 확인해 주세요."}
                    </CustomTypography>
                  )}
                </Stack>

                {error && <ErrorText message={error} />}

                <Button
                  variant="Action"
                  size="lg"
                  disabled={!infoFilled || submitting}
                  loading={submitting}
                  onClick={handlePreview}
                  sx={{
                    width: "100%",
                    height: "54px",
                    fontWeight: 700,
                    borderRadius: "12px",
                    fontSize: "16px",
                  }}
                >
                  {"계약서 확인하기"}
                </Button>
              </Stack>
            )}

            {/* Step 2 — 핵심 내용 확인: 숙지가 필요하고 오해가 있으면 안 되는 내용을 headline+요점으로 보여준다.
                내용은 서버(keyTerms, 템플릿 버전 단위)가 내려준다 — 요약-본문 불일치 방지.
                세무 처리·개인정보 동의는 정산 정보 수집 시점(서류 제출 단계)에 받는다 */}
            {step === "terms" && preview && (
              <Stack spacing={"12px"} sx={{ mt: 3 }}>
                {/* 당사자 확인 카드 — 입력값 오타를 확인 전에 잡는다 */}
                <Box
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                    px: "16px",
                    py: "14px",
                  }}
                >
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      fontWeight: 700,
                      color: theme.vars.palette.Content.Highlight,
                    }}
                  >
                    {"계약 당사자를 확인해 주세요"}
                  </CustomTypography>
                  <Stack spacing={"6px"} sx={{ mt: "8px" }}>
                    <KeyCheckRow label="계약형태" value={contractTypeLabel} />
                    <KeyCheckRow
                      label="계약 당사자"
                      value={
                        isBusiness
                          ? `${businessName.trim()} (대표 ${representativeName.trim()} · ${formatBrn(brnDigits)})`
                          : realName.trim()
                      }
                    />
                    <KeyCheckRow label="주소" value={fullAddress} />
                  </Stack>
                </Box>

                {(preview.keyTerms ?? []).map((section) => (
                  <KeyTermCard key={section.key} section={section} />
                ))}

                <CustomTypography
                  variant="Footnote"
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Weak,
                  }}
                >
                  {
                    "위 정리는 이해를 돕기 위한 것으로, 계약 내용은 다음 단계의 전문을 기준으로 해요."
                  }
                </CustomTypography>

                <AgreeItem
                  checked={agreedKeyTerms}
                  onChange={setAgreedKeyTerms}
                  text="내용을 모두 확인했습니다."
                />

                {error && <ErrorText message={error} />}

                <Button
                  variant="Action"
                  size="lg"
                  disabled={!agreedKeyTerms}
                  onClick={() => {
                    setReadCompleted(false);
                    setReadProgress(0);
                    setAgreedFull(false);
                    setStep("read");
                    setError(null);
                  }}
                  sx={{
                    width: "100%",
                    height: "54px",
                    fontWeight: 700,
                    borderRadius: "12px",
                    fontSize: "16px",
                  }}
                >
                  {"전문 확인하기"}
                </Button>
              </Stack>
            )}

            {/* Step 3 — 전문 확인·체결: 계약서 지면(종이 문서) 위에서 전문을 끝까지 읽은 뒤
                마지막으로 포괄 동의(agreedFull)를 받고 체결한다. 상단 바가 읽기 진행률을 보여준다 */}
            {step === "read" && preview && (
              <Stack spacing={"16px"} sx={{ mt: 3 }}>
                <Box
                  sx={{
                    borderRadius: "12px",
                    border: `1px solid ${theme.vars.palette.Border.Weak}`,
                    overflow: "hidden",
                  }}
                >
                  <LinearProgress
                    determinate
                    value={readProgress}
                    sx={{
                      "--LinearProgress-thickness": "3px",
                      "--LinearProgress-radius": "0px",
                      color: theme.vars.palette.Content.Highlight,
                    }}
                  />
                  <Box
                    ref={readBoxRef}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      const bottom = el.scrollTop + el.clientHeight;
                      setReadProgress(
                        Math.min(
                          100,
                          Math.round((bottom / el.scrollHeight) * 100)
                        )
                      );
                      if (bottom >= el.scrollHeight - READ_BOTTOM_MARGIN_PX) {
                        setReadCompleted(true);
                      }
                    }}
                    sx={{
                      height: "56vh",
                      overflowY: "auto",
                      // 종이 문서 메타포 — 테마와 무관하게 밝은 지면으로 고정한다 (ContractDocument 와 페어)
                      backgroundColor: "#ffffff",
                      px: { xs: "20px", sm: "32px" },
                      py: "24px",
                    }}
                  >
                    <ContractDocument source={preview.content} />
                  </Box>
                </Box>
                {!readCompleted && (
                  <CustomTypography
                    variant="Footnote"
                    sx={{
                      textAlign: "center",
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {"계약서를 끝까지 읽으면 체결 동의를 할 수 있어요."}
                  </CustomTypography>
                )}
                {/* 전문 포괄 동의 — 전문 최하단 도달 전에는 체크할 수 없다 */}
                <AgreeItem
                  checked={agreedFull}
                  onChange={setAgreedFull}
                  disabled={!readCompleted}
                  text="계약서 전문을 모두 확인했으며, 위 내용으로 계약 체결에 동의합니다."
                />
                {error && <ErrorText message={error} />}
                <Button
                  variant="Action"
                  size="lg"
                  disabled={!allAgreed || submitting}
                  onClick={() => setConfirming(true)}
                  sx={{
                    width: "100%",
                    height: "54px",
                    fontWeight: 700,
                    borderRadius: "12px",
                    fontSize: "16px",
                  }}
                >
                  {"전자계약 체결하기"}
                </Button>
              </Stack>
            )}

            {/* 주소 검색 시트 — 모바일은 하단 시트(85vh), 데스크톱은 중앙 카드. 팝업 차단 이슈 없이 동작한다 */}
            {addressSearchOpen && (
              <Box
                onClick={() => setAddressSearchOpen(false)}
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 1300,
                  backgroundColor: "rgba(0, 0, 0, 0.4)",
                  display: "flex",
                  alignItems: { xs: "flex-end", sm: "center" },
                  justifyContent: "center",
                }}
              >
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", sm: "480px" },
                    height: { xs: "85vh", sm: "560px" },
                    backgroundColor: theme.vars.palette.Surface.Forward,
                    borderRadius: { xs: "16px 16px 0 0", sm: "16px" },
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: "16px", py: "12px" }}
                  >
                    <CustomTypography
                      variant="Body"
                      sx={{
                        fontWeight: 700,
                        color: theme.vars.palette.Content.Strong,
                      }}
                    >
                      {"주소 검색"}
                    </CustomTypography>
                    <CloseRoundedIcon
                      titleAccess="닫기"
                      onClick={() => setAddressSearchOpen(false)}
                      sx={{
                        fontSize: "22px",
                        color: theme.vars.palette.Content.Medium,
                        cursor: "pointer",
                      }}
                    />
                  </Stack>
                  {/* 다음 우편번호 검색이 임베드되는 영역 */}
                  <Box ref={postcodeBoxRef} sx={{ flex: 1, minHeight: 0 }} />
                </Box>
              </Box>
            )}

            {/* 체결 전 확인 팝업 — 체결 후에는 내용을 변경할 수 없다 */}
            {confirming && (
              <CustomModal
                open
                onClose={() => setConfirming(false)}
                title="전자계약을 체결할까요?"
                subtitle="체결 후에는 계약 내용을 변경할 수 없어요. 체결 시각과 동의 내역이 기록돼요."
                actions={[
                  {
                    label: "체결하기",
                    onClick: handleSign,
                    variant: "Solid",
                    buttonProps: { loading: submitting },
                  },
                  {
                    label: "취소",
                    onClick: () => setConfirming(false),
                    variant: "Outlined",
                  },
                ]}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// 체결본 열람 뷰 — 저장된 스냅샷 전문을 계약서 지면으로 표시하고, 다운로드는 서버 렌더링 PDF 로 제공한다.
function SignedContractView({
  content,
  signedAt,
  completed,
  documentsSubmitted,
  onHome,
  onDocuments,
}: {
  content: string | null;
  signedAt: string | null;
  completed: boolean;
  documentsSubmitted: boolean;
  onHome: () => void;
  onDocuments: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // 서버가 스냅샷을 계약서 양식(A4)으로 렌더링한 PDF 를 받는다. 비즈니스 오류는 JSON Blob 으로 온다.
  const handleDownloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await ambassadorApi.getContractSignPdf();
      if (blob.type !== "application/pdf") {
        const parsed = JSON.parse(await blob.text());
        setDownloadError(parsed?.message || FALLBACK_ERROR_MESSAGE);
        return;
      }
      const date = signedAt?.slice(0, 10).replace(/-/g, "") ?? "";
      saveBlobAsFile(
        blob,
        `디스턴싱_앰버서더_전자계약서${date ? `_${date}` : ""}.pdf`
      );
    } catch (e) {
      setDownloadError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing={"16px"}>
      <Box sx={{ display: "flex" }}>
        <LinkV2 variant="Caption" text="← 홈으로" onClick={onHome} />
      </Box>
      <Stack spacing={"8px"}>
        <CustomTypography
          variant="H3"
          sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
        >
          {"체결된 전자계약서"}
        </CustomTypography>
        <CustomTypography
          variant="Caption"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Medium }}
        >
          {`체결 시각: ${formatSignedAt(signedAt)} · ${
            completed ? "계약 완료" : "담당자 확인 중"
          }`}
        </CustomTypography>
      </Stack>
      {!completed && !documentsSubmitted && (
        <Box
          onClick={onDocuments}
          sx={{
            cursor: "pointer",
            borderRadius: "12px",
            backgroundColor: theme.vars.palette.Surface.SoftHighlight,
            px: "16px",
            py: "12px",
          }}
        >
          <CustomTypography
            variant="Caption"
            sx={{
              fontWeight: 500,
              color: theme.vars.palette.Content.Highlight,
            }}
          >
            {"정산 정보와 서류를 제출해 주세요 — 확인 후 계약이 완료돼요 →"}
          </CustomTypography>
        </Box>
      )}
      {content ? (
        <>
          <Box
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              borderRadius: "12px",
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              // 종이 문서 메타포 — 테마와 무관하게 밝은 지면으로 고정한다 (ContractDocument 와 페어)
              backgroundColor: "#ffffff",
              px: { xs: "20px", sm: "32px" },
              py: "24px",
            }}
          >
            <ContractDocument source={content} />
          </Box>
          <Stack spacing={"8px"}>
            <Button
              variant="Outlined"
              size="lg"
              loading={downloading}
              onClick={handleDownloadPdf}
              sx={{
                width: "fit-content",
                fontWeight: 700,
                borderRadius: "12px",
              }}
            >
              {"계약서 PDF 다운로드"}
            </Button>
            {downloadError && <ErrorText message={downloadError} />}
          </Stack>
        </>
      ) : (
        <CustomTypography
          variant="Caption"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
        >
          {"체결본을 불러오지 못했어요. 잠시 후 다시 시도해 주세요."}
        </CustomTypography>
      )}
    </Stack>
  );
}

// 핵심 내용 카드 — headline(숫자·기간)을 크게, 보조 요점을 작은 불릿으로 보여준다.
// 제외·반환 조건 카드도 같은 중립 톤 — 경고색 프레이밍은 체결 직전 이탈을 유발해 쓰지 않는다 (사실은 전부 노출).
function KeyTermCard({
  section,
}: {
  section: AmbassadorContractKeyTermSection;
}) {
  const items = section.items ?? [];
  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        backgroundColor: theme.vars.palette.Surface.Forward,
        px: "16px",
        py: "14px",
      }}
    >
      <CustomTypography
        variant="Footnote"
        sx={{
          fontWeight: 700,
          color: theme.vars.palette.Content.Medium,
        }}
      >
        {section.title}
      </CustomTypography>
      {section.headline && (
        <CustomTypography
          variant="Body"
          sx={{
            mt: "4px",
            fontWeight: 700,
            fontSize: "17px",
            color: theme.vars.palette.Content.Strong,
            wordBreak: "keep-all",
            overflowWrap: "anywhere",
          }}
        >
          {section.headline}
        </CustomTypography>
      )}
      {items.length > 0 && (
        <Stack spacing={"6px"} sx={{ mt: "8px" }}>
          {items.map((item) => (
            <Stack
              key={item}
              direction="row"
              spacing={"6px"}
              alignItems="flex-start"
            >
              <CustomTypography
                variant="Footnote"
                sx={{
                  flexShrink: 0,
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Weak,
                }}
              >
                {"•"}
              </CustomTypography>
              <CustomTypography
                variant="Footnote"
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                  wordBreak: "keep-all",
                  overflowWrap: "anywhere",
                }}
              >
                {item}
              </CustomTypography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// 동의 체크 행 — 체크박스 + 문구 (문구 클릭으로도 토글). disabled 면 조작 불가 (전문 미열람 등 선행 조건).
function AgreeItem({
  checked,
  onChange,
  text,
  linkText,
  onLink,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  text: string;
  linkText?: string;
  onLink?: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Checkbox
        size="sm"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ mt: "2px" }}
      />
      <CustomTypography
        variant="Caption"
        sx={{ fontWeight: 500, color: theme.vars.palette.Content.Medium }}
      >
        <Box
          component="span"
          sx={{ cursor: disabled ? "default" : "pointer" }}
          onClick={() => !disabled && onChange(!checked)}
        >
          {text}
        </Box>
        {linkText && onLink && (
          <Box
            component="span"
            sx={{
              ml: "6px",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={onLink}
          >
            {linkText}
          </Box>
        )}
      </CustomTypography>
    </Box>
  );
}

// 체결 전 확인 카드의 라벨/값 행 — 값은 줄바꿈으로 전체 노출한다 (말줄임 금지, 작은 화면 대응).
function KeyCheckRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={"8px"} alignItems="flex-start">
      <CustomTypography
        variant="Caption"
        sx={{
          flexShrink: 0,
          width: "76px",
          fontWeight: 500,
          color: theme.vars.palette.Content.Medium,
        }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="Caption"
        sx={{
          fontWeight: 700,
          color: theme.vars.palette.Content.Strong,
          wordBreak: "keep-all",
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </CustomTypography>
    </Stack>
  );
}

// 사업자등록번호 표기 (숫자 10자리 → 000-00-00000)
function formatBrn(digits: string): string {
  if (digits.length !== 10) return digits;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function FieldLabel({ text }: { text: string }) {
  return (
    <CustomTypography
      variant="Caption"
      sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
    >
      {text}
    </CustomTypography>
  );
}

function ErrorText({ message }: { message: string }) {
  return (
    <CustomTypography
      variant="Caption"
      sx={{
        fontWeight: 500,
        color: theme.vars.palette.Content.HighlightWithAlert,
      }}
    >
      {message}
    </CustomTypography>
  );
}

// 체결 시각(ISO) → "2026. 8. 13. 14:05" 표기. 파싱 실패 시 원문 그대로.
function formatSignedAt(signedAt: string | null): string {
  if (!signedAt) return "-";
  const date = new Date(signedAt);
  if (Number.isNaN(date.getTime())) return signedAt;
  return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}. ${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
}
